// ============================================================================
// MarketMasterWalletPage — кошелёк мастера (профи-пилот маркетплейса).
//
// Один мобильный экран: доступно/на счёте/в резервах, пополнение через
// существующий платёжный слой и история операций строками («Списано за
// заявку №…», «Возврат за заявку №…», пополнения, резервы).
//
// Без таблиц и модальных окон: карточки, крупные касания, одна колонка.
// Деньги не кэшируются — при обрыве связи показывается явное состояние.
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { RefreshCw, Wallet } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { MarketTopBar } from '@/components/market/MarketTopBar';
import { useI18n } from '@/lib/i18n/I18nProvider';
import { MarketError, MarketLoading } from '@/components/market/MarketState';
import { MarketWalletHolds } from '@/components/market/MarketWalletHolds';
import { MarketWalletOperations } from '@/components/market/MarketWalletOperations';
import { MarketWalletSummary } from '@/components/market/MarketWalletSummary';
import { MarketWalletTopUp } from '@/components/market/MarketWalletTopUp';
import { useAuthStore } from '@/store/authStore';
import { eventBusHub } from '@/lib/eventBusHub';
import { logErrorToJournal } from '@/lib/errors';
import {
  loadMarketWallet,
  type MarketWalletResult,
} from '@/lib/marketWallet';

interface MarketMasterWalletPageProps {
  onBack: () => void;
}

export function MarketMasterWalletPage({ onBack }: MarketMasterWalletPageProps) {
  const userId = String(useAuthStore((s) => s.user?.id || ''));
  const { t } = useI18n();
  const [result, setResult] = useState<MarketWalletResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [tick, setTick] = useState(0);

  const load = useCallback(async () => {
    setRefreshing(true);
    const next = await loadMarketWallet(userId);
    setResult(next);
    if (!next.ok) {
      logErrorToJournal(
        new Error(next.message),
        `Кошелёк мастера: загрузка (${next.kind})`,
      );
    }
    setLoading(false);
    setRefreshing(false);
  }, [userId]);

  useEffect(() => {
    setLoading(true);
    void load();
  }, [load, tick]);

  // Живое обновление: операции биллинга и изменения заявок (взятие/возврат).
  // Поллинга нет — только события серверной шины.
  useEffect(() => {
    return eventBusHub.subscribe((ev) => {
      if (
        ev.topic.startsWith('billing.')
        || ev.topic.startsWith('data.document.mkt_requests.')
      ) {
        setTick((n) => n + 1);
      }
    });
  }, []);

  const refresh = () => setTick((n) => n + 1);

  return (
    <div className="flex h-full flex-col">
      <MarketTopBar
        title={t('market.wallet')}
        subtitle={t('market.wallet_subtitle')}
        onBack={onBack}
        action={(
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 rounded-lg p-0"
            onClick={refresh}
            disabled={refreshing}
            aria-label={t('market.refresh')}
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
        )}
      />

      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-2xl space-y-4 p-4 pb-24 sm:p-6">
          {loading && !result ? (
            <MarketLoading text={t('market.wallet_loading')} />
          ) : result?.ok ? (
            <>
              <MarketWalletSummary
                data={result.data}
                refreshing={refreshing}
                onRefresh={refresh}
                onTopUp={() => {
                  document.getElementById('market-wallet-topup')?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                  });
                }}
              />
              <MarketWalletHolds
                holds={result.data.holds}
                currency={result.data.balance.currency}
              />
              <MarketWalletTopUp
                providers={result.data.providers}
                providersError={result.data.providersError}
                disabled={false}
                currency={result.data.balance.currency}
                userId={userId}
                onDone={(paid) => {
                  if (paid) refresh();
                }}
              />
              <MarketWalletOperations
                operations={result.data.operations}
                currency={result.data.balance.currency}
              />
            </>
          ) : result && !result.ok && result.kind === 'disabled' ? (
            <div className="rounded-3xl border border-amber-500/30 bg-amber-500/5 p-4">
              <div className="flex items-start gap-2.5">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-500/15 text-amber-700">
                  <Wallet className="h-4 w-4" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-amber-900">{t('market.billing_off')}</div>
                  <div className="mt-1 break-words text-xs leading-5 text-amber-800">
                    {result.message}
                  </div>
                  <div className="mt-2 text-[11px] leading-5 text-amber-700">
                    Личный счёт и списания за заявки недоступны, пока администратор базы
                    не включит биллинг (Администрирование → Биллинг).
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-3 h-8 rounded-lg"
                    onClick={refresh}
                    disabled={refreshing}
                  >
                    <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
                    Повторить
                  </Button>
                </div>
              </div>
            </div>
          ) : result && !result.ok ? (
            <>
              <MarketError
                failure={{
                  kind: result.kind === 'offline'
                    ? 'offline'
                    : result.kind === 'permission'
                      ? 'permission'
                      : 'other',
                  message: result.message,
                }}
                onRetry={refresh}
              />
              {result.kind === 'offline' && (
                <MarketWalletTopUp
                  providers={[]}
                  providersError=""
                  disabled
                  currency=""
                  userId={userId}
                  onDone={() => undefined}
                />
              )}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
